import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import PhoneInput from '../components/PhoneInput';
import { getGeoLocation, submitLead, getDialCodeFromCountryCode } from '../services/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });
  const [countryDialCode, setCountryDialCode] = useState('1');
  const [geoData, setGeoData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch geolocation on mount
  useEffect(() => {
    const fetchGeoLocation = async () => {
      const data = await getGeoLocation();
      if (data && data.isSuccess) {
        setGeoData(data);
        const dialCode = getDialCodeFromCountryCode(data.data.countryCode);
        setCountryDialCode(dialCode.toString());
      }
    };
    fetchGeoLocation();
  }, []);

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    return '';
  };

  // Phone validation
  const validatePhone = (phone) => {
    const digitsOnly = phone.replace(/\D/g, '');
    
    if (!phone) {
      return 'Phone number is required';
    }
    if (digitsOnly.length !== 10) {
      return 'Phone number must be exactly 10 digits after country code';
    }
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate email and phone
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);
    
    setErrors({
      email: emailError,
      phone: phoneError
    });

    // If there are validation errors, don't submit
    if (emailError || phoneError) {
      toast.error('Please fix the errors before submitting');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare lead data
      const leadPayload = {
        name: formData.name,
        email: formData.email,
        countryDialCode: parseInt(countryDialCode),
        country: geoData?.data?.countryCode || 'US',
        phoneNumber: formData.phone, // Already numeric only from validation
        ipAddress: geoData?.data?.ipAddress || '',
        description: `Service: ${formData.service || 'General inquiry'} - ${formData.message || ''}`,
        pageUrl: window.location.href,
        city: geoData?.data?.cityName || '',
        state: geoData?.data?.regionName || '',
        geoLocation: geoData ? JSON.stringify(geoData) : '',
        packageTitle: formData.service || '',
        packageAmount: 0
      };

      // Submit lead
      const response = await submitLead(leadPayload);

      console.log('API Response:', response);

      if (response.statusCode === 200 && response.data?.data?.customerId) {
        // Success - redirect to application
        toast.success(response.data.data.message || 'Thank you! We will contact you soon.');
        setTimeout(() => {
          window.location.href = `/application/main?customerid=${response.data.data.customerId}`;
        }, 1500);
      } else if (response.statusCode === 401) {
        // Unauthorized
        toast.error(response.data?.message || 'Authorization error');
      } else if (response.statusCode === 400) {
        // Bad request
        toast.error(response.data?.errorMessage || response.message || 'Invalid data provided');
      } else {
        // Other errors
        toast.error(response.message || 'Something went wrong. Please try again.');
      }
    } catch (error) {
      toast.error('An unexpected error occurred. Please try again.');
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    
    // Clear error when user starts typing
    if (name === 'email') {
      setErrors(prev => ({ ...prev, email: '' }));
    }
  };

  const handlePhoneChange = (e) => {
    setFormData({ ...formData, phone: e.target.value });
    // Clear error when user starts typing
    setErrors(prev => ({ ...prev, phone: '' }));
  };

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-b from-slate-900 to-slate-950 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6">
              Get in <span className="text-transparent bg-clip-text bg-gradient-to-r from-amber-400 to-amber-600">Touch</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              Have questions about trademark registration or our services? We're here to help. Reach out to schedule a free consultation with our expert team.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-20 bg-slate-950">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <Card className="bg-slate-900/60 border-white/20">
                <CardHeader>
                  <CardTitle className="text-white text-2xl">Send Us a Message</CardTitle>
                  <CardDescription className="text-gray-400">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <Label htmlFor="name" className="text-gray-300">Full Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2"
                        placeholder="John Doe"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-gray-300">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className={`bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2 ${errors.email ? 'border-red-500' : ''}`}
                        placeholder="john@example.com"
                      />
                      {errors.email && (
                        <p className="text-red-500 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <PhoneInput
                        id="phone"
                        value={formData.phone}
                        onChange={handlePhoneChange}
                        onCountryChange={(code) => setCountryDialCode(code)}
                        defaultCountryCode="+1"
                        className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40"
                        error={errors.phone}
                      />
                      {errors.phone && (
                        <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-gray-300">Service Interested In *</Label>
                      <Select
                        value={formData.service}
                        onValueChange={(value) => setFormData({ ...formData, service: value })}
                        required
                      >
                        <SelectTrigger className="bg-slate-800 border-white/20 text-white focus:border-white/40 mt-2">
                          <SelectValue placeholder="Select a service" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-white/20">
                          <SelectItem value="trademark-registration" className="text-white hover:bg-slate-700">Trademark Registration</SelectItem>
                          <SelectItem value="copyright-registration" className="text-white hover:bg-slate-700">Copyright Registration</SelectItem>
                          <SelectItem value="company-formation" className="text-white hover:bg-slate-700">Company Formation</SelectItem>
                          <SelectItem value="trademark-search" className="text-white hover:bg-slate-700">Trademark Search</SelectItem>
                          <SelectItem value="amazon-brand-registry" className="text-white hover:bg-slate-700">Amazon Brand Registry</SelectItem>
                          <SelectItem value="other" className="text-white hover:bg-slate-700">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-gray-300">Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="bg-slate-800 border-white/20 text-white placeholder:text-gray-500 focus:border-white/40 mt-2"
                        placeholder="Tell us about your trademark or IP needs..."
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold shadow-lg"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-white mb-6">Contact Information</h2>
                <p className="text-gray-400 mb-8">
                  Prefer to reach out directly? Use any of the contact methods below. We're available to answer your questions and help you protect your brand.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-3 rounded-lg">
                        <MapPin className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Office Address</h3>
                        <p className="text-gray-400 text-sm">
                          12100 Wilshire Blvd 8th floor<br />
                          Los Angeles, CA 90025<br />
                          United States
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-3 rounded-lg">
                        <Phone className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Phone</h3>
                        <a href="tel:+18332020812" className="text-gray-400 hover:text-amber-500 transition-colors text-sm">
                          +1-833-202-0812
                        </a>
                        <p className="text-gray-500 text-xs mt-1">Toll-free</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-3 rounded-lg">
                        <Mail className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Email</h3>
                        <a href="mailto:info@globalfilingprotection.com" className="text-gray-400 hover:text-amber-500 transition-colors text-sm">
                          info@globalfilingprotection.com
                        </a>
                        <p className="text-gray-500 text-xs mt-1">We respond within 24 hours</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-slate-900/60 border-white/20 hover:border-white/40 hover:shadow-lg hover:shadow-amber-500/20 hover:scale-[1.02] transition-colors">
                  <CardContent className="pt-6">
                    <div className="flex items-start space-x-4">
                      <div className="bg-gradient-to-br from-amber-500/10 to-amber-600/10 p-3 rounded-lg">
                        <Clock className="h-6 w-6 text-amber-500" />
                      </div>
                      <div>
                        <h3 className="text-white font-semibold mb-1">Business Hours</h3>
                        <p className="text-gray-400 text-sm">
                          Monday - Friday: 9:00 AM - 6:00 PM EST<br />
                          Saturday: 10:00 AM - 2:00 PM EST<br />
                          Sunday: Closed
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card className="bg-gradient-to-br from-slate-900 to-slate-800 border-white/20">
                <CardContent className="pt-6">
                  <h3 className="text-white font-semibold mb-2">Emergency Contact</h3>
                  <p className="text-gray-400 text-sm mb-3">
                    For urgent trademark matters or time-sensitive issues, please call our emergency line.
                  </p>
                  <a href="tel:+18332020812" className="text-amber-500 font-semibold hover:text-amber-400 transition-colors">
                    +1-833-202-0812
                  </a>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
